import { useEffect, useRef, useState } from 'react';
import { KeywordForm } from '@indipia/ui-core';
import { worklogApi } from '../api/worklogApi';
import { ThumbImg } from './ThumbImg';
import { PHOTO_POLICY } from '../config/uploadPolicy';
import type { MyKeyword } from '../types/worklog.types';

/**
 * 작업요청 "전달할 작업내용" 섹션 공용 부품 — 내키워드(§2.7~2.10) + 사진첨부(파일·인디포토).
 * 단건 작성(WorkOrderCreatePage)·일괄 작성(WorkOrderBulkPage) 둘 다 같은 UI/동작이라 여기로 뺐다.
 */

/** 인디포토 에이전트 agentId (TM_AGENT A01) — 구독 여부 판정용 */
export const INDI_PHOTO_AGENT_ID = 1;

/**
 * 첨부 사진 1건 — 출처 2종(§2.3 `source`).
 * - FILE: 로컬 파일 업로드. `file` 보유, `url` 은 objectURL 미리보기.
 * - INDI_PHOTO: 인디포토 사진첩 선택. 파일 없이 `photoId` 만 전송하고, `url` 은 인증 필요한 썸네일 경로.
 */
export interface LocalPhoto {
  id: string;
  url: string;
  name: string;
  source: 'FILE' | 'INDI_PHOTO';
  /** 실제 이미지 파일 — 전송 시 photoFiles[] 로 업로드 (source=FILE) */
  file?: File;
  /** 인디포토ID — 파일 대신 이 값으로 등록 (source=INDI_PHOTO) */
  photoId?: number;
  /** 인디포토 원본 파일ID — 인디포토가 내려주면 photoId 와 함께 저장한다 (source=INDI_PHOTO) */
  fileId?: number | string | null;
}

/** 미리보기 정리 — objectURL(파일 첨부분)만 해제한다. 인디포토는 서버 URL이라 해제 대상이 아니다. */
export function revokePreview(p: LocalPhoto) {
  if (p.source === 'FILE') URL.revokeObjectURL(p.url);
}

/** 첨부 사진 미리보기 — 파일은 objectURL, 인디포토는 인증 필요한 썸네일(blob 로더) */
export function PhotoPreview({ photo, className }: { photo: LocalPhoto; className: string }) {
  if (photo.source === 'INDI_PHOTO') {
    return <ThumbImg url={photo.url} className={className} alt={photo.name} />;
  }
  return <img src={photo.url} alt={photo.name} className={className} />;
}

// ─────────────────────────────────────────────────────────────────────────
// 내키워드 — 조회/삽입/추가/삭제/순서변경 (API §2.7~2.10)
// ─────────────────────────────────────────────────────────────────────────
export function KeywordChips({ onInsert }: { onInsert: (text: string) => void }) {
  const [keywords, setKeywords] = useState<MyKeyword[]>([]);

  const load = async () => {
    try {
      const res = await worklogApi.getMyKeywords();
      setKeywords(res.data.data.items ?? []);
    } catch {
      /* 조회 실패 시 빈 목록 유지 */
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const add = async (name: string) => {
    try {
      await worklogApi.addMyKeyword(name);
      await load();
    } catch {
      alert('키워드 추가에 실패했습니다.');
    }
  };

  const remove = async (id: number) => {
    const prev = keywords;
    setKeywords((k) => k.filter((x) => x.keywordId !== id));
    try {
      await worklogApi.deleteMyKeyword(id);
    } catch {
      setKeywords(prev);
      alert('키워드 삭제에 실패했습니다.');
    }
  };

  const commitOrder = async (next: MyKeyword[]) => {
    const prev = keywords;
    setKeywords(next);
    try {
      const res = await worklogApi.reorderMyKeywords(next.map((k) => k.keywordId));
      setKeywords(res.data.data.items ?? next);
    } catch {
      // 409(순서 불일치 등) → 목록 재조회로 복구
      setKeywords(prev);
      await load();
    }
  };

  const reorder = (from: number, to: number) => {
    const next = [...keywords];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    void commitOrder(next);
  };

  return (
    <div className="flex flex-1 flex-col">
      <KeywordForm
        tags={keywords.map((k) => k.worklogKeyword)}
        onAdd={(v) => void add(v)}
        onRemove={(i) => void remove(keywords[i].keywordId)}
        onReorder={reorder}
        onTagClick={(tag) => onInsert(tag)}
        placeholder="# 키워드 입력 후 엔터"
        help={keywords.length ? '칩 클릭: 내용에 삽입 · 드래그: 순서 변경' : '자주 쓰는 문구를 등록해 두면 클릭 한 번으로 삽입돼요.'}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 사진첨부 — 파일 업로드(미리보기) + 인디포토 사진첩 선택
// ─────────────────────────────────────────────────────────────────────────
export function PhotoAttach({
  photos,
  onAdd,
  onRemove,
  onOpen,
  onOpenIndiPhoto,
}: {
  photos: LocalPhoto[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
  onOpen: (index: number) => void;
  onOpenIndiPhoto: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-1 flex-wrap items-center gap-2">
      {/* 첨부 썸네일 — 버튼 앞에 표시 */}
      {photos.map((p, i) => (
        <span key={p.id} className="group relative">
          <button
            type="button"
            onClick={() => onOpen(i)}
            aria-label={`${p.name} 크게보기`}
            className="block h-11 w-11 overflow-hidden rounded-lg border-base border-border transition-colors hover:border-group"
          >
            <PhotoPreview photo={p} className="h-full w-full object-cover" />
          </button>
          <button
            type="button"
            aria-label="삭제"
            onClick={() => onRemove(p.id)}
            className="absolute -right-1.5 -top-1.5 flex h-[17px] w-[17px] items-center justify-center rounded-full border-2 border-surface-raised bg-text-secondary text-[9px] font-bold leading-none text-white transition-colors hover:bg-error"
          >
            ×
          </button>
        </span>
      ))}

      {/*
        사진 추가 트리거는 썸네일과 같은 58×58 타일이라 Button 기반 FileInput 대신
        숨김 input + 타일 버튼으로 직접 구성한다.
        용량/개수 검증은 input(maxSize)이 아니라 pickValidPhotos에 맡긴다 —
        이 화면 정책은 "초과분만 제외하고 나머지는 첨부 + 토스트 안내"다.
      */}
      {photos.length < PHOTO_POLICY.maxCount && (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex h-11 w-11 flex-col items-center justify-center gap-1 rounded-lg border-[1.5px] border-dashed border-border text-text-muted transition-colors hover:border-group hover:text-group"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span className="text-caption leading-none">사진 추가</span>
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onAdd(files);
          e.target.value = ''; // 같은 파일 재선택 허용
        }}
      />
      {/* 인디포토 사진첩 — 선택 팝업 열기. 사진 추가 타일과 같은 규격으로 나란히 둔다. */}
      <button
        type="button"
        onClick={onOpenIndiPhoto}
        disabled={photos.length >= PHOTO_POLICY.maxCount}
        title="인디포토 사진첩에서 선택"
        className="flex h-11 w-11 flex-col items-center justify-center gap-1 rounded-lg border-[1.5px] border-dashed border-border text-text-muted transition-colors hover:border-group hover:text-group disabled:cursor-not-allowed disabled:text-text-disabled disabled:hover:border-border"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        <span className="text-caption leading-none">인디포토</span>
      </button>

      {/* 타일 높이(58px)에 맞춰 2줄로 표시 */}
      <span className="ml-1 flex flex-col text-caption leading-snug text-text-muted">
        <span>최대 {PHOTO_POLICY.maxCount}장</span>
        <span>장당 {PHOTO_POLICY.maxMB}MB 이내</span>
      </span>
    </div>
  );
}
